import React, { useEffect, useState } from 'react';
import { TextField, Button, Paper, Typography, CardActions, IconButton, Box, useTheme, Grid2, Divider } from '@mui/material';
import { Cancel, Info, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Announcements, AnnouncementsPriority, AnnouncementStatus, APICallStatus } from '../../Types/UnitFeatureTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Actions, RootState } from '../../Redux/Reducer/rootReducer';
import DropdownSelection from '../CommonComponents/DropdownSelection';
import { announcementId, createAnnouncementAPIStatus, updateAnnouncementAPIStatus } from '../../Redux/selectors';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Editor from '../TextEditor/Editor';
import LoadingOverlay from '../CommonComponents/LoadingOverlay';
import { useApp } from '../../context/appContext';

const CreateAnnouncement: React.FC = () => {
  const [formData, setFormData] = useState<Announcements>({
    title: '',
    description: '',
    created_on: '',
    priority: AnnouncementsPriority.INFORMATIONAL,
    id: 0,
    status: AnnouncementStatus.DRAFT,
    seen_by: 0,
  })
  const [title, setTitle] = useState<string>("Create Announcement");
  const location = useLocation();
  const dispatch = useDispatch();
  const [sendAnnouncement, setSendAnnouncement] = useState<boolean>(false);
  const createdAnnouncementId = useSelector(announcementId);
  const isLoading = useSelector((state: RootState) => state.announcement.request.loading);
  const theme = useTheme();

  const createAnnouncementAPI = useSelector(createAnnouncementAPIStatus);
  const editAnnouncementAPI = useSelector(updateAnnouncementAPIStatus);
  const { setSnackbar, setLoading } = useApp();


  useEffect(() => {
    if (createAnnouncementAPI === APICallStatus.SUCCESS) {
      setLoading(false);
      setSnackbar(true, 'Announcement created successfully', 'success');
      setSendAnnouncement(false);
    } else if (createAnnouncementAPI === APICallStatus.FAILURE) {
      setLoading(false);
      setSnackbar(true, 'Failed to create announcement', 'error');
    }

    if (editAnnouncementAPI === APICallStatus.SUCCESS) {
      setLoading(false);
      setSnackbar(true, 'Announcement updated successfully', 'success');
      setSendAnnouncement(true);
    } else if (editAnnouncementAPI === APICallStatus.FAILURE) {
      setLoading(false);
      setSnackbar(true, 'Failed to update announcement', 'error');
    }

    if (editAnnouncementAPI === APICallStatus.SUCCESS || createAnnouncementAPI === APICallStatus.SUCCESS || editAnnouncementAPI === APICallStatus.FAILURE || createAnnouncementAPI === APICallStatus.FAILURE) {
      dispatch(Actions.announcement.resetStatusRequestAction());
    }
  }, [createAnnouncementAPI, editAnnouncementAPI, sendAnnouncement]);

  useEffect(() => {

    if (location?.state?.copy || location?.state?.back) {
      let announcement = location.state.announcement as Announcements;
      setFormData((prevState) => ({
        ...prevState,
        title: `Copy of ${announcement.title}`,
        description: announcement.description,
        priority: AnnouncementsPriority[announcement.priority] as AnnouncementsPriority,
      }));
    } else if (location?.state?.edit) {
      let announcement = location.state.announcement as Announcements;
      setFormData((prevState) => ({
        ...prevState,
        title: `${announcement.title}`,
        description: announcement.description,
        priority: AnnouncementsPriority[announcement.priority] as AnnouncementsPriority,
        id: announcement.id,
        created_on: announcement.created_on,
        status: announcement.status,
        seen_by: announcement.seen_by,
      }));
      setTitle("Edit Announcement");
    } else {
      setFormData({
        title: '',
        description: '',
        created_on: '',
        priority: AnnouncementsPriority.INFORMATIONAL,
        status: AnnouncementStatus.DRAFT,
        seen_by: 0,
        id: 0,
      });
    }
  }, []);

  const navigate = useNavigate();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    // const descriptionJson = formData.description && JSON.parse(formData.description);
    // const descriptionText = descriptionJson?.root?.children?.[0]?.children?.[0]?.text;
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';
    // if (!descriptionText || descriptionText.trim() === '') newErrors.description = 'Description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const  handleSubmit = (e: React.FormEvent, isDraft?: boolean) => {
    e.preventDefault();
    if (!validateForm()) return;
    setErrors({});
    if (location?.state?.edit && formData.id) {
      // dispatch(Actions.announcement.editAnnouncementRequestAction.request({ announcementData: formData }));
      return;
    } else {
      dispatch(Actions.announcement.createAnnouncementRequestAction.request({ announcementData: formData }));
    
      if (isDraft) {
        navigate(-1);
      } else {
        navigate('/sendAnnouncement', { state: { announcementId: createdAnnouncementId } });
      }
    }
  }
  const handleOnChangeDescription = (value: any) => {
    setFormData((prevState) => ({
      ...prevState,
      description: value,
    }))
  };
  

  return (
    <>
    <LoadingOverlay isLoading={isLoading} />
    <Grid2 container spacing={2} sx={{ mb: 2 }}>
      <Grid2 size={{xs: 12 }}>
    <Paper elevation={5} sx={{ padding: 6, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <IconButton 
          sx={{ '&:hover': {
            color: 'error.main',
          } }}
          onClick={() => navigate(-1)}>
          <Cancel />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Title"
          variant="outlined"
          name="title"
          placeholder='Please provide a title for the issue.'
          margin='normal'
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title}
        />
        <Editor 
          value={formData.description} 
          placeholder='Please write the details of the announcement here.'
          error={!!errors.description}
          helperText={errors.description}
          onChange={handleOnChangeDescription}
        />
        <DropdownSelection
          dropDownLabel='Priority'
          showHeader={false}
          labelSx={{mt:1.5}}
          currentValue={formData.priority}
          selectionValues={[
            { value: AnnouncementsPriority.INFORMATIONAL, label: 'Informational', color: 'grey', icon: Info },
            { value: AnnouncementsPriority.LOW, label: 'Low', color: 'blue', icon: KeyboardArrowDown },
            { value: AnnouncementsPriority.MEDIUM, label: 'Medium', color: theme.palette.warning.main, icon: KeyboardArrowUp },
            { value: AnnouncementsPriority.HIGH, label: 'High', color: 'red', icon: KeyboardArrowUp },
          ]}
          variant='outlined'
          sxObject={{ mb: 2, mt: 1.5}}
          handleOnChange={(e) => setFormData((prevState) => ({
            ...prevState,
            priority: e.target.value as AnnouncementsPriority,
          }))}
        />
          {/* <AttachmentUpload /> */}
        <CardActions sx={{ justifyContent: 'space-between' }}>
            <Button fullWidth variant="contained" color="secondary" onClick={(e) => handleSubmit(e)}>
                Save Draft
            </Button>
            <Button fullWidth onClick={(e) => {setSendAnnouncement(true); handleSubmit(e);}} variant="contained" color="primary">
                Send
            </Button>
        </CardActions>
      </form>
    </Paper>
    </Grid2>
    </Grid2>
    </>
  );
};

export default CreateAnnouncement;
